import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import api from '../../config/api';
import Footer from '../../components/Footer';
import './SubscriptionScreen.css';

const STRIPE_PUBLISHABLE_KEY = 'pk_live_51S2juGPyMTxsGXRZmym8mgPEHQrDTl7cultEF8Issr0ctdN9sqF1GrPqWxaGaqkqBt10bSjfeIHYvGV6LUMEBCmk00hrmOBzc5';
const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

function SubscriptionScreen() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadSubscriptionStatus();
  }, []);

  const loadSubscriptionStatus = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/stripe/subscription-status');
      setSubscription(data);
    } catch (err) {
      console.error('Failed to load subscription:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchClientSecret = useCallback(async () => {
    const { data } = await api.post('/stripe/create-checkout-session');
    if (!data?.clientSecret) {
      throw new Error('Failed to start checkout');
    }
    return data.clientSecret;
  }, []);

  const checkoutOptions = useMemo(() => ({ fetchClientSecret }), [fetchClientSecret]);

  const handleStartCheckout = async () => {
    setError('');
    setCheckoutLoading(true);
    setShowCheckout(true);
  };

  const handleManageSubscription = async () => {
    try {
      setLoading(true);
      const { data } = await api.post('/stripe/create-portal-session');
      window.location.href = data.url;
    } catch (err) {
      setError('Failed to open subscription management. Please try again.');
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="sub-page">
        <div className="sub-loader">
          <div className="sub-spinner" />
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  const hasActiveSubscription = subscription?.hasActiveSubscription;
  const isTrialing = subscription?.isTrialing;

  /* ── Active Subscription View ── */
  if (hasActiveSubscription) {
    return (
      <div className="sub-page">
        <div className="sub-wrap">
          <button className="sub-back" onClick={() => navigate('/home')}>← Back to Home</button>

          <div className="sub-active-card">
            <div className="sub-active-badge">
              <span className="sub-active-check">✓</span>
              Premium Active
            </div>
            <h1>You're all set!</h1>
            {subscription?.source === 'google_play' ? (
              <p className="sub-active-detail">
                Managed through Google Play.
                {subscription.endDate && ` Active until ${new Date(subscription.endDate).toLocaleDateString()}.`}
              </p>
            ) : isTrialing ? (
              <p className="sub-active-detail">
                Free trial active — billing starts {new Date(subscription.endDate).toLocaleDateString()}.
              </p>
            ) : (
              <p className="sub-active-detail">
                Next billing date: {new Date(subscription.endDate).toLocaleDateString()}.
              </p>
            )}
            {subscription.cancelAtPeriodEnd && (
              <p className="sub-cancel-note">Cancels at end of current period.</p>
            )}

            <div className="sub-perks">
              {['Unlimited Swipes','$1 Cashback Per Order','All Modes','Partner Linking','Decision History','Custom Preferences','Priority Support'].map((p) => (
                <span key={p} className="sub-perk"><span className="sub-perk-dot" />{p}</span>
              ))}
            </div>

            {subscription?.source !== 'google_play' && (
              <button className="sub-manage-btn" onClick={handleManageSubscription}>
                Manage Subscription
              </button>
            )}
            {subscription?.source === 'google_play' && (
              <p className="sub-gp-note">Manage in the Google Play Store app on your Android device.</p>
            )}
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  /* ── Embedded Checkout View (stays on our domain) ── */
  if (showCheckout) {
    return (
      <div className="sub-page">
        <div className="sub-wrap">
          <button
            className="sub-back"
            onClick={() => { setShowCheckout(false); setCheckoutLoading(false); }}
          >
            ← Back
          </button>

          {error && <div className="sub-error">{error}</div>}

          <div style={{ background: '#fff', borderRadius: '12px', overflow: 'hidden', minHeight: '500px' }}>
            <EmbeddedCheckoutProvider stripe={stripePromise} options={checkoutOptions}>
              <EmbeddedCheckout />
            </EmbeddedCheckoutProvider>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  /* ── Offer / Pricing View ── */
  return (
    <div className="sub-page">
      <div className="sub-wrap">
        <button className="sub-back" onClick={() => navigate('/home')}>← Back to Home</button>

        {error && <div className="sub-error">{error}</div>}

        <div className="sub-hero">
          <h1>Hangrily <span>Premium</span></h1>
          <p>Stop arguing about where to eat. Start swiping — and earn $1 cashback on every order.</p>
        </div>

        <div className="sub-pricing-row">
          <div className="sub-price-card sub-price-card--main">
            <div className="sub-price-top">
              <span className="sub-price-amount">$4.99</span>
              <span className="sub-price-period">/month</span>
            </div>
          </div>
        </div>

        <div className="sub-features">
          {[
            { icon: '💰', title: '$1 Cashback Per Order', desc: 'Upload a receipt, get cash back' },
            { icon: '🎯', title: 'Unlimited Swipes', desc: 'No limits on decisions' },
            { icon: '👥', title: 'All Modes', desc: 'Solo, Couple & Group' },
            { icon: '💑', title: 'Partner Linking', desc: 'Instant couple sessions' },
            { icon: '📊', title: 'Decision History', desc: 'Track your favorites' },
            { icon: '⚙️', title: 'Custom Preferences', desc: 'Save cuisine & filters' },
            { icon: '🚀', title: 'Priority Support', desc: 'Help when you need it' },
          ].map((f) => (
            <div key={f.title} className="sub-feature">
              <span className="sub-feature-icon">{f.icon}</span>
              <div>
                <strong>{f.title}</strong>
                <small>{f.desc}</small>
              </div>
            </div>
          ))}
        </div>

        <button
          className="sub-cta"
          onClick={handleStartCheckout}
          disabled={checkoutLoading}
        >
          {checkoutLoading ? 'Loading…' : 'Subscribe Now'}
        </button>
        <p className="sub-legal">$4.99/month. Cancel anytime.</p>

        <button className="sub-skip" onClick={() => navigate('/home')}>
          Maybe Later — Continue Free
        </button>

        <div className="sub-why">
          <h3>Why Premium?</h3>
          <p>
            We're a small team building the best restaurant decision app.
            Your subscription keeps us going — no ads, no data selling,
            just a tool that helps you decide where to eat.
          </p>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default SubscriptionScreen;
